import { NgModule } from '@angular/core';
import { Apollo, APOLLO_NAMED_OPTIONS, NamedOptions } from 'apollo-angular';
import {
  HttpHeaders,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache, DocumentNode } from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { print } from 'graphql';
import { RetryLink } from '@apollo/client/link/retry';
import { ErrorLink } from '@apollo/client/link/error';
import { ConfigurationService } from '../configuration/services/configuration/configuration.service';
import { LoggingService } from 'src/app/shared/services/logging/logging.service';
import { MessageService } from '../message/services/message/message.service';
import { MessageCodes } from 'src/app/shared/enums';
import { environment } from 'src/environments/environment';
import { AwsCredentialsService } from 'src/app/shared/services/aws-credentials/aws-credentials.service';
import { createSigV4Link } from './sigv4-link';

const RETRY_STATUSES = [0, 500, 501, 502, 503];
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Build a link for an AppSync endpoint that uses the specified
 * auth: IAM/SigV4 or API key.
 *
 * @param uri URL of the GraphQL service endpoint
 * @param usingIamAuth whether to use IAM auth
 * @param awsCreds AwsCredentialsService to use
 * @param httpLink HttpLink to use
 * @param apiKey API key for the GraphQL service
 */
export function buildAppSyncLink(
  uri: string,
  usingIamAuth: boolean,
  awsCreds: AwsCredentialsService,
  httpLink: HttpLink,
  apiKey?: string
): ApolloLink {
  return usingIamAuth
    ? createSigV4Link(uri, awsCreds)
    : httpLink.create({
        uri,
        headers: new HttpHeaders({ 'x-api-key': apiKey ?? '' }),
      });
}

@NgModule({
  providers: [
    Apollo,
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory(
        c: ConfigurationService,
        httpLink: HttpLink,
        loggingService: LoggingService,
        messageService: MessageService,
        awsCreds: AwsCredentialsService
      ): NamedOptions {
        const logQueryFailure = (
          operationName: string,
          errorMessage: string,
          errorStatus: number | null,
          query: DocumentNode,
          variables: Record<string, unknown>
        ): void => {
          loggingService.logInfo('graphql_query_failed', {
            operationName,
            errorMessage,
            errorStatus,
            query: print(query),
            variables,
          });
        };
        const showErrorMessage = (statusCode: number | null): void => {
          if (statusCode && Object.values(MessageCodes).includes(statusCode)) {
            messageService.showMessage(statusCode as MessageCodes);
          } else {
            messageService.showMessage(MessageCodes.APP_UNKNOWN_ERROR);
          }
        };
        const errorLink = new ErrorLink(({ error, operation }) => {
          if (CombinedGraphQLErrors.is(error)) {
            logQueryFailure(
              operation.operationName || 'anonymous',
              error.errors.map(e => e.message).join('; '),
              200,
              operation.query,
              operation.variables
            );
            showErrorMessage(200);
          } else if (error) {
            const statusCode =
              'statusCode' in error
                ? (error as unknown as { statusCode: number }).statusCode
                : null;
            logQueryFailure(
              operation.operationName || 'anonymous',
              error.message,
              statusCode,
              operation.query,
              operation.variables
            );
            showErrorMessage(statusCode);
          }
        });
        const retryLink = new RetryLink({
          delay: {
            initial: 2000,
            max: 8000,
            jitter: true,
          },
          attempts: (count, operation, error): boolean => {
            const operationName = operation.operationName || 'anonymous';
            const statusCode =
              (error as unknown as { statusCode?: number })?.statusCode ??
              (error as unknown as { status?: number })?.status;
            const shouldRetry =
              !statusCode || RETRY_STATUSES.includes(statusCode);
            if (!shouldRetry || count > MAX_RETRY_ATTEMPTS) {
              return false;
            }
            console.warn(
              `Retrying GraphQL operation "${operationName}" (attempt ${count}/${MAX_RETRY_ATTEMPTS})`,
              error
            );
            return true;
          },
        });
        return {
          contentMetadata: {
            cache: new InMemoryCache(),
            link: ApolloLink.from([
              errorLink,
              retryLink,
              httpLink.create({
                uri: c.config.contentMetadataDomain + 'graphql/',
              }),
            ]),
          },
          studentInbox: {
            cache: new InMemoryCache(),
            link: ApolloLink.from([
              errorLink,
              retryLink,
              buildAppSyncLink(
                c.config.studentRecordStoreDomain + 'graphql/',
                c.usingIamAuth,
                awsCreds,
                httpLink,
                environment.AMIRA_SRS_API_KEY
              ),
            ]),
          },
          amiraSIS: {
            cache: new InMemoryCache(),
            link: ApolloLink.from([
              errorLink,
              retryLink,
              buildAppSyncLink(
                c.config.amiraSISUrl + 'graphql/',
                c.usingIamAuth,
                awsCreds,
                httpLink,
                environment.AMIRA_SIS_API_KEY
              ),
            ]),
          },
        };
      },
      deps: [
        ConfigurationService,
        HttpLink,
        LoggingService,
        MessageService,
        AwsCredentialsService,
      ],
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class GraphqlModule {}
